import {Injectable} from '@angular/core';
import {UserRestService} from "../../../api/services/user.rest.service";
import {EMPTY, Observable, of} from "rxjs";
import {catchError, map, mergeMap} from "rxjs/operators";
import {UserApiModel} from "../../../api/models/user.api-model";
import {UserPasswordViewModel} from "./new-password/user-password.view-model";
import {UserViewModel} from "./user.view-model";
import {CurrentUserViewModel} from "../../../core/models/current-user.view-model";
import {MediaObjectRestService} from "../../../api/services/media-object.rest.service";
import {MediaObjectApiModel} from "../../../api/models/media-object.api-model";
import {MediaObjectViewModel} from "../../../core/models/media-object.view-model";
import {QueryParamsApiModel} from "../../../api/models/query-params-api.model";
import {FilterParameterApiModel} from "../../../api/models/filter-parameter.api.model";
import {ResultListApiModel} from "../../../api/models/result-list.api.model";
import {HttpErrorResponse} from "@angular/common/http";
import {Router} from "@angular/router";

@Injectable()
export class UserDataProviderService {

  constructor(
    private us: UserRestService,
    private mors: MediaObjectRestService,
    private router: Router
  ) {
  }

  public getUser(id: string): Observable<UserViewModel> {
    return this.us.get(id).pipe(map((userApiModel: UserApiModel) => new UserViewModel(userApiModel)))
  }

  public getUserWithMedia(id: string): Observable<UserViewModel> {
    return this.us.get(id).pipe(
      mergeMap((userApiModel: UserApiModel) => {
        const userVM = new UserViewModel(userApiModel);
        const params = new QueryParamsApiModel();
        if (userApiModel.avatar) {
          params.filters.push(new FilterParameterApiModel('id', userApiModel.avatar));
        }
        if (userApiModel.background) {
          params.filters.push(new FilterParameterApiModel('id', userApiModel.background));
        }
        if (!params.filters.length) {
          return of(userVM);
        }
        return this.mors.getMediaObjectCollection(params)
          .pipe(map((mediaObjectResultList: ResultListApiModel<MediaObjectApiModel>) => {
            const avatar = mediaObjectResultList.records.find(e => e["@id"] === userApiModel.avatar);
            const background = mediaObjectResultList.records.find(e => e["@id"] === userApiModel.background);
            userVM.avatar = avatar ? new MediaObjectViewModel(avatar) : null;
            userVM.background = background ? new MediaObjectViewModel(background) : null;
            return userVM;
          }));
      }),
      catchError((errorResponse: HttpErrorResponse) => {
        switch (errorResponse.status) {
          case 500:
            this.router.navigate(['/500']);
            return EMPTY;
        }
        return EMPTY;
      })
    );
  }

  public getUserWithMediaBySlug(slug: string): Observable<UserViewModel> {
    return this.getUserWithMedia(`${this.us.resource}/${slug}`);
  }

  public updateUser(userViewModel: UserViewModel): Observable<UserViewModel> {
    return this.us.put(userViewModel["@id"], userViewModel.toApiModel())
      .pipe(map((userApiModel: UserApiModel) => userViewModel.fromApiModel(userApiModel)));
  }

  public updateUserSocialProfileLinks(userViewModel: UserViewModel): Observable<UserViewModel> {
    return this.us.putSocialLinks(userViewModel["@id"], userViewModel.toApiModel())
      .pipe(map((userApiModel: UserApiModel) => userViewModel.fromApiModel(userApiModel)));
  }

  public updateSettings(userViewModel: UserViewModel): Observable<UserViewModel> {
    return this.us.putSettings(userViewModel["@id"], userViewModel.toApiModel())
      .pipe(map((userApiModel: UserApiModel) => userViewModel.fromApiModel(userApiModel)));
  }

  public changePassword(userViewModel: UserPasswordViewModel) {
    return this.us.changePassword(userViewModel.toPasswordApiModel())
  }

  public deleteAccount(password: string) {
    return this.us.deleteAccount(password);
  }

  public updateUserAvatar(currentUserViewModel: CurrentUserViewModel, file: File): Observable<CurrentUserViewModel> {
    return this.mors.uploadImage(file)
      .pipe(mergeMap((mediaObjectApiModel: MediaObjectApiModel) => {
        const currentUserApiModel = currentUserViewModel.toApiModel();
        currentUserApiModel.avatar = mediaObjectApiModel['@id'];
        return this.us.patch(currentUserViewModel['@id'], currentUserApiModel)
          .pipe(map((userApiModel: UserApiModel) => {
            currentUserViewModel.fromApiModel(userApiModel);
            currentUserViewModel.avatar = new MediaObjectViewModel(mediaObjectApiModel);
            return currentUserViewModel;
          }))
      }));
  }

  public removeUserAvatar(currentUserViewModel: CurrentUserViewModel): Observable<CurrentUserViewModel> {
    return this.mors.deleteMediaObject(currentUserViewModel.avatar["@id"])
      .pipe(map(() => {
        currentUserViewModel.avatar = null;
        return currentUserViewModel;
      }));
  }

  public updateUserBackground(currentUserViewModel: CurrentUserViewModel, file: File): Observable<CurrentUserViewModel> {
    return this.mors.uploadImage(file)
      .pipe(mergeMap((mediaObjectApiModel: MediaObjectApiModel) => {
        const currentUserApiModel = currentUserViewModel.toApiModel();
        currentUserApiModel.background = mediaObjectApiModel['@id'];
        return this.us.patch(currentUserViewModel['@id'], currentUserApiModel)
          .pipe(map((userApiModel: UserApiModel) => {
            currentUserViewModel.fromApiModel(userApiModel);
            currentUserViewModel.background = new MediaObjectViewModel(mediaObjectApiModel);
            return currentUserViewModel;
          }))
      }));
  }

  public removeUserBackground(currentUserViewModel: CurrentUserViewModel): Observable<CurrentUserViewModel> {
    return this.mors.deleteMediaObject(currentUserViewModel.background["@id"])
      .pipe(map(() => {
        currentUserViewModel.background = null;
        return currentUserViewModel;
      }));
  }
}
